/**
 * Central language definitions used by the language combo boxes
 * across ProjectsComponent (Draft, Book, Product settings),
 * ProductComponent, and anywhere else a user picks or types a
 * language for a work.
 *
 * Shape:
 *   iso639_3    – 3-letter ISO 639-3 code (always present; primary
 *                 storage value for `languageCode` on Draft, Book,
 *                 and Product)
 *   iso639_2    – 2-letter ISO 639-1 code where one exists; empty
 *                 string otherwise. (Field is named `iso639_2` for
 *                 legacy reasons — semantically it is ISO 639-1.)
 *   name        – Human-readable English name (also serves as the
 *                 LanguagePipe key when translatable=true).
 *   translatable – Optional. When true, callers should feed `name`
 *                 through LanguagePipe.get(name) to get the locale
 *                 translation; when omitted/false the raw English
 *                 `name` is displayed and there is no reason to try
 *                 to look up a translation.
 *
 * Ordering: entries that have a 2-letter code appear first, sorted
 * by the 2-letter code; entries without a 2-letter code appear
 * afterward, sorted by the 3-letter code. This is the ordering the
 * combo boxes present to the user.
 *
 * The `translatable: true` set intentionally tracks the site's
 * supported UI locales (see the `<lang>prod.bat` scripts —
 * en, es, fr, de, ar, am, zh, ko, ru, ro). Add a language here
 * when the site itself is translated into it AND the `name` key is
 * present in `src/locale/messages.en.xtb`.
 */

export interface LanguageEntry {
  iso639_3: string;
  iso639_2: string; // ISO 639-1 (2-letter) — empty string when none exists.
  name: string;
  translatable?: boolean;
}

/**
 * Full canonical list of languages. Entries with an ISO 639-1
 * 2-letter code come first (sorted by that code), followed by
 * entries with only a 3-letter ISO 639-3 code (sorted by that
 * code).
 *
 * The 2-letter-code section below is complete — every ISO 639-1
 * assignment is represented. The 3-letter-only section starts
 * with a representative slice; extend it here (or via
 * `tools/build-languages.js` if generating from a source dump)
 * without disturbing the 2-letter section above it.
 */
export const LANGUAGES: LanguageEntry[] = [
  // ── ISO 639-1 (2-letter code) — sorted by 2-letter code ──
  { iso639_3: 'aar', iso639_2: 'aa', name: 'Afar' },
  { iso639_3: 'abk', iso639_2: 'ab', name: 'Abkhaz' },
  { iso639_3: 'afr', iso639_2: 'af', name: 'Afrikaans', translatable: true },
  { iso639_3: 'aka', iso639_2: 'ak', name: 'Akan' },
  { iso639_3: 'amh', iso639_2: 'am', name: 'Amharic', translatable: true },
  { iso639_3: 'arb', iso639_2: 'ar', name: 'Arabic, Standard', translatable: true },
  { iso639_3: 'arg', iso639_2: 'an', name: 'Aragonese' },
  { iso639_3: 'asm', iso639_2: 'as', name: 'Assamese' },
  { iso639_3: 'ava', iso639_2: 'av', name: 'Avar' },
  { iso639_3: 'ave', iso639_2: 'ae', name: 'Avestan' },
  { iso639_3: 'aym', iso639_2: 'ay', name: 'Aymara' },
  { iso639_3: 'azj', iso639_2: 'az', name: 'Azerbaijani' },
  { iso639_3: 'bak', iso639_2: 'ba', name: 'Bashkort' },
  { iso639_3: 'bam', iso639_2: 'bm', name: 'Bamanankan' },
  { iso639_3: 'bel', iso639_2: 'be', name: 'Belarusan' },
  { iso639_3: 'ben', iso639_2: 'bn', name: 'Bengali' },
  { iso639_3: 'bis', iso639_2: 'bi', name: 'Bislama' },
  { iso639_3: 'bod', iso639_2: 'bo', name: 'Tibetan' },
  { iso639_3: 'bos', iso639_2: 'bs', name: 'Bosnian' },
  { iso639_3: 'bre', iso639_2: 'br', name: 'Breton' },
  { iso639_3: 'bul', iso639_2: 'bg', name: 'Bulgarian' },
  { iso639_3: 'cat', iso639_2: 'ca', name: 'Catalan' },
  { iso639_3: 'ces', iso639_2: 'cs', name: 'Czech' },
  { iso639_3: 'cha', iso639_2: 'ch', name: 'Chamorro' },
  { iso639_3: 'che', iso639_2: 'ce', name: 'Chechen' },
  { iso639_3: 'chu', iso639_2: 'cu', name: 'Church Slavic' },
  { iso639_3: 'chv', iso639_2: 'cv', name: 'Chuvash' },
  { iso639_3: 'cor', iso639_2: 'kw', name: 'Cornish' },
  { iso639_3: 'cos', iso639_2: 'co', name: 'Corsican' },
  { iso639_3: 'cre', iso639_2: 'cr', name: 'Cree' },
  { iso639_3: 'cym', iso639_2: 'cy', name: 'Welsh' },
  { iso639_3: 'dan', iso639_2: 'da', name: 'Danish' },
  { iso639_3: 'deu', iso639_2: 'de', name: 'German, Standard', translatable: true },
  { iso639_3: 'div', iso639_2: 'dv', name: 'Maldivian' },
  { iso639_3: 'dzo', iso639_2: 'dz', name: 'Dzongkha' },
  { iso639_3: 'ell', iso639_2: 'el', name: 'Greek' },
  { iso639_3: 'eng', iso639_2: 'en', name: 'English', translatable: true },
  { iso639_3: 'epo', iso639_2: 'eo', name: 'Esperanto' },
  { iso639_3: 'est', iso639_2: 'et', name: 'Estonian' },
  { iso639_3: 'eus', iso639_2: 'eu', name: 'Basque' },
  { iso639_3: 'ewe', iso639_2: 'ee', name: 'Éwé' },
  { iso639_3: 'fao', iso639_2: 'fo', name: 'Faroese' },
  { iso639_3: 'fij', iso639_2: 'fj', name: 'Fijian' },
  { iso639_3: 'fin', iso639_2: 'fi', name: 'Finnish' },
  { iso639_3: 'fra', iso639_2: 'fr', name: 'French', translatable: true },
  { iso639_3: 'fry', iso639_2: 'fy', name: 'Frisian, Western' },
  { iso639_3: 'ful', iso639_2: 'ff', name: 'Fula' },
  { iso639_3: 'gla', iso639_2: 'gd', name: 'Gaelic, Scottish' },
  { iso639_3: 'gle', iso639_2: 'ga', name: 'Gaelic, Irish' },
  { iso639_3: 'glg', iso639_2: 'gl', name: 'Galician' },
  { iso639_3: 'glv', iso639_2: 'gv', name: 'Manx' },
  { iso639_3: 'grn', iso639_2: 'gn', name: 'Guaraní' },
  { iso639_3: 'guj', iso639_2: 'gu', name: 'Gujarati' },
  { iso639_3: 'hat', iso639_2: 'ht', name: 'Haitian Creole French' },
  { iso639_3: 'hau', iso639_2: 'ha', name: 'Hausa' },
  { iso639_3: 'heb', iso639_2: 'he', name: 'Hebrew' },
  { iso639_3: 'her', iso639_2: 'hz', name: 'Herero' },
  { iso639_3: 'hin', iso639_2: 'hi', name: 'Hindi' },
  { iso639_3: 'hmo', iso639_2: 'ho', name: 'Motu, Hiri' },
  { iso639_3: 'hrv', iso639_2: 'hr', name: 'Croatian' },
  { iso639_3: 'hun', iso639_2: 'hu', name: 'Hungarian' },
  { iso639_3: 'hye', iso639_2: 'hy', name: 'Armenian' },
  { iso639_3: 'ibo', iso639_2: 'ig', name: 'Igbo' },
  { iso639_3: 'ido', iso639_2: 'io', name: 'Ido' },
  { iso639_3: 'iii', iso639_2: 'ii', name: 'Nuosu' },
  { iso639_3: 'iku', iso639_2: 'iu', name: 'Inuktitut' },
  { iso639_3: 'ile', iso639_2: 'ie', name: 'Interlingue' },
  { iso639_3: 'ina', iso639_2: 'ia', name: 'Interlingua' },
  { iso639_3: 'ind', iso639_2: 'id', name: 'Indonesian' },
  { iso639_3: 'ipk', iso639_2: 'ik', name: 'Inupiaq' },
  { iso639_3: 'isl', iso639_2: 'is', name: 'Icelandic' },
  { iso639_3: 'ita', iso639_2: 'it', name: 'Italian' },
  { iso639_3: 'jav', iso639_2: 'jv', name: 'Javanese' },
  { iso639_3: 'jpn', iso639_2: 'ja', name: 'Japanese' },
  { iso639_3: 'kal', iso639_2: 'kl', name: 'Inuktitut, Greenlandic' },
  { iso639_3: 'kan', iso639_2: 'kn', name: 'Kannada' },
  { iso639_3: 'kas', iso639_2: 'ks', name: 'Kashmiri' },
  { iso639_3: 'kat', iso639_2: 'ka', name: 'Georgian' },
  { iso639_3: 'kau', iso639_2: 'kr', name: 'Kanuri' },
  { iso639_3: 'kaz', iso639_2: 'kk', name: 'Kazakh' },
  { iso639_3: 'khm', iso639_2: 'km', name: 'Khmer, Central' },
  { iso639_3: 'kik', iso639_2: 'ki', name: 'Gikuyu' },
  { iso639_3: 'kin', iso639_2: 'rw', name: 'Rwanda' },
  { iso639_3: 'kir', iso639_2: 'ky', name: 'Kyrgyz' },
  { iso639_3: 'kom', iso639_2: 'kv', name: 'Komi' },
  { iso639_3: 'kon', iso639_2: 'kg', name: 'Kongo' },
  { iso639_3: 'kor', iso639_2: 'ko', name: 'Korean', translatable: true },
  { iso639_3: 'kua', iso639_2: 'kj', name: 'Kwanyama' },
  { iso639_3: 'kur', iso639_2: 'ku', name: 'Kurdish' },
  { iso639_3: 'lao', iso639_2: 'lo', name: 'Lao' },
  { iso639_3: 'lat', iso639_2: 'la', name: 'Latin' },
  { iso639_3: 'lav', iso639_2: 'lv', name: 'Latvian' },
  { iso639_3: 'lim', iso639_2: 'li', name: 'Limburgish' },
  { iso639_3: 'lin', iso639_2: 'ln', name: 'Lingala' },
  { iso639_3: 'lit', iso639_2: 'lt', name: 'Lithuanian' },
  { iso639_3: 'ltz', iso639_2: 'lb', name: 'Luxembourgeois' },
  { iso639_3: 'lub', iso639_2: 'lu', name: 'Luba-Katanga' },
  { iso639_3: 'lug', iso639_2: 'lg', name: 'Ganda' },
  { iso639_3: 'mah', iso639_2: 'mh', name: 'Marshallese' },
  { iso639_3: 'mal', iso639_2: 'ml', name: 'Malayalam' },
  { iso639_3: 'mar', iso639_2: 'mr', name: 'Marathi' },
  { iso639_3: 'mkd', iso639_2: 'mk', name: 'Macedonian' },
  { iso639_3: 'mlg', iso639_2: 'mg', name: 'Malagasy' },
  { iso639_3: 'mlt', iso639_2: 'mt', name: 'Maltese' },
  { iso639_3: 'mly', iso639_2: 'ms', name: 'Malay' },
  { iso639_3: 'mon', iso639_2: 'mn', name: 'Mongolian' },
  { iso639_3: 'mri', iso639_2: 'mi', name: 'Maori' },
  { iso639_3: 'mya', iso639_2: 'my', name: 'Burmese' },
  { iso639_3: 'nau', iso639_2: 'na', name: 'Nauruan' },
  { iso639_3: 'nav', iso639_2: 'nv', name: 'Navajo' },
  { iso639_3: 'nbl', iso639_2: 'nr', name: 'Ndebele, South' },
  { iso639_3: 'nde', iso639_2: 'nd', name: 'Ndebele, North' },
  { iso639_3: 'ndo', iso639_2: 'ng', name: 'Ndonga' },
  { iso639_3: 'nep', iso639_2: 'ne', name: 'Nepali' },
  { iso639_3: 'nld', iso639_2: 'nl', name: 'Dutch' },
  { iso639_3: 'nno', iso639_2: 'nn', name: 'Norwegian, Nynorsk' },
  { iso639_3: 'nob', iso639_2: 'nb', name: 'Norwegian, Bokmål' },
  { iso639_3: 'nor', iso639_2: 'no', name: 'Norwegian' },
  { iso639_3: 'nya', iso639_2: 'ny', name: 'Nyanja' },
  { iso639_3: 'oci', iso639_2: 'oc', name: 'Occitan' },
  { iso639_3: 'oji', iso639_2: 'oj', name: 'Ojibwa' },
  { iso639_3: 'ori', iso639_2: 'or', name: 'Oriya' },
  { iso639_3: 'orm', iso639_2: 'om', name: 'Oromo' },
  { iso639_3: 'oss', iso639_2: 'os', name: 'Osetin' },
  { iso639_3: 'pan', iso639_2: 'pa', name: 'Panjabi, Eastern' },
  { iso639_3: 'pes', iso639_2: 'fa', name: 'Farsi, Western' },
  { iso639_3: 'pli', iso639_2: 'pi', name: 'Pali' },
  { iso639_3: 'pol', iso639_2: 'pl', name: 'Polish' },
  { iso639_3: 'por', iso639_2: 'pt', name: 'Portuguese' },
  { iso639_3: 'pus', iso639_2: 'ps', name: 'Pashto' },
  { iso639_3: 'que', iso639_2: 'qu', name: 'Quechua' },
  { iso639_3: 'roh', iso639_2: 'rm', name: 'Romansch' },
  { iso639_3: 'ron', iso639_2: 'ro', name: 'Romanian', translatable: true },
  { iso639_3: 'run', iso639_2: 'rn', name: 'Rundi' },
  { iso639_3: 'rus', iso639_2: 'ru', name: 'Russian', translatable: true },
  { iso639_3: 'sag', iso639_2: 'sg', name: 'Sango' },
  { iso639_3: 'san', iso639_2: 'sa', name: 'Sanskrit' },
  { iso639_3: 'sin', iso639_2: 'si', name: 'Sinhala' },
  { iso639_3: 'slk', iso639_2: 'sk', name: 'Slovak' },
  { iso639_3: 'slv', iso639_2: 'sl', name: 'Slovenian' },
  { iso639_3: 'sme', iso639_2: 'se', name: 'Saami, North' },
  { iso639_3: 'smo', iso639_2: 'sm', name: 'Samoan' },
  { iso639_3: 'sna', iso639_2: 'sn', name: 'Shona' },
  { iso639_3: 'snd', iso639_2: 'sd', name: 'Sindhi' },
  { iso639_3: 'som', iso639_2: 'so', name: 'Somali' },
  { iso639_3: 'sot', iso639_2: 'st', name: 'Sotho, Southern' },
  { iso639_3: 'spa', iso639_2: 'es', name: 'Spanish', translatable: true },
  { iso639_3: 'sqi', iso639_2: 'sq', name: 'Albanian' },
  { iso639_3: 'srd', iso639_2: 'sc', name: 'Sardinian' },
  { iso639_3: 'srp', iso639_2: 'sr', name: 'Serbian' },
  { iso639_3: 'ssw', iso639_2: 'ss', name: 'Swati' },
  { iso639_3: 'sun', iso639_2: 'su', name: 'Sunda' },
  { iso639_3: 'swa', iso639_2: 'sw', name: 'Swahili' },
  { iso639_3: 'swe', iso639_2: 'sv', name: 'Swedish' },
  { iso639_3: 'tah', iso639_2: 'ty', name: 'Tahitian' },
  { iso639_3: 'tam', iso639_2: 'ta', name: 'Tamil' },
  { iso639_3: 'tat', iso639_2: 'tt', name: 'Tatar' },
  { iso639_3: 'tel', iso639_2: 'te', name: 'Telugu' },
  { iso639_3: 'tgk', iso639_2: 'tg', name: 'Tajiki' },
  { iso639_3: 'tgl', iso639_2: 'tl', name: 'Tagalog' },
  { iso639_3: 'tha', iso639_2: 'th', name: 'Thai' },
  { iso639_3: 'tir', iso639_2: 'ti', name: 'Tigrigna' },
  { iso639_3: 'ton', iso639_2: 'to', name: 'Tongan' },
  { iso639_3: 'tsn', iso639_2: 'tn', name: 'Tswana' },
  { iso639_3: 'tso', iso639_2: 'ts', name: 'Tsonga' },
  { iso639_3: 'tuk', iso639_2: 'tk', name: 'Turkmen' },
  { iso639_3: 'tur', iso639_2: 'tr', name: 'Turkish' },
  { iso639_3: 'twi', iso639_2: 'tw', name: 'Twi' },
  { iso639_3: 'uig', iso639_2: 'ug', name: 'Uyghur' },
  { iso639_3: 'ukr', iso639_2: 'uk', name: 'Ukrainian' },
  { iso639_3: 'urd', iso639_2: 'ur', name: 'Urdu' },
  { iso639_3: 'uzn', iso639_2: 'uz', name: 'Uzbek' },
  { iso639_3: 'ven', iso639_2: 've', name: 'Venda' },
  { iso639_3: 'vie', iso639_2: 'vi', name: 'Vietnamese' },
  { iso639_3: 'vol', iso639_2: 'vo', name: 'Volapük' },
  { iso639_3: 'wln', iso639_2: 'wa', name: 'Walloon' },
  { iso639_3: 'wol', iso639_2: 'wo', name: 'Wolof' },
  { iso639_3: 'xho', iso639_2: 'xh', name: 'Xhosa' },
  { iso639_3: 'yid', iso639_2: 'yi', name: 'Yiddish' },
  { iso639_3: 'yor', iso639_2: 'yo', name: 'Yoruba' },
  { iso639_3: 'zha', iso639_2: 'za', name: 'Zhuang' },
  { iso639_3: 'zho', iso639_2: 'zh', name: 'Chinese', translatable: true },
  { iso639_3: 'zul', iso639_2: 'zu', name: 'Zulu' },

  // ── ISO 639-3 only (no 2-letter code) — sorted by 3-letter code ──
  // The full 7000+ entry set from the ISO 639-3 registry can be
  // appended here alphabetically; the entries below are the most
  // frequently-selected non-ISO-639-1 languages we've observed in
  // the catalogue. Extend as needed — the sort order (by
  // `iso639_3` ascending) must be preserved.
  { iso639_3: 'ace', iso639_2: '', name: 'Aceh' },
  { iso639_3: 'ach', iso639_2: '', name: 'Acholi' },
  { iso639_3: 'ang', iso639_2: '', name: 'English, Old' },
  { iso639_3: 'arc', iso639_2: '', name: 'Aramaic, Official' },
  { iso639_3: 'arn', iso639_2: '', name: 'Mapudungun' },
  { iso639_3: 'arp', iso639_2: '', name: 'Arapaho' },
  { iso639_3: 'awa', iso639_2: '', name: 'Awadhi' },
  { iso639_3: 'bal', iso639_2: '', name: 'Balochi' },
  { iso639_3: 'ban', iso639_2: '', name: 'Balinese' },
  { iso639_3: 'bho', iso639_2: '', name: 'Bhojpuri' },
  { iso639_3: 'bik', iso639_2: '', name: 'Bikol' },
  { iso639_3: 'bin', iso639_2: '', name: 'Edo' },
  { iso639_3: 'bua', iso639_2: '', name: 'Buriat' },
  { iso639_3: 'bug', iso639_2: '', name: 'Bugis' },
  { iso639_3: 'byn', iso639_2: '', name: 'Bilen' },
  { iso639_3: 'ceb', iso639_2: '', name: 'Cebuano' },
  { iso639_3: 'chk', iso639_2: '', name: 'Chuukese' },
  { iso639_3: 'chp', iso639_2: '', name: 'Dene' },
  { iso639_3: 'chr', iso639_2: '', name: 'Cherokee' },
  { iso639_3: 'chy', iso639_2: '', name: 'Cheyenne' },
  { iso639_3: 'crh', iso639_2: '', name: 'Crimean Tatar' },
  { iso639_3: 'dgr', iso639_2: '', name: 'Dogrib' },
  { iso639_3: 'doi', iso639_2: '', name: 'Dogri' },
  { iso639_3: 'efi', iso639_2: '', name: 'Efik' },
  { iso639_3: 'fil', iso639_2: '', name: 'Filipino' },
  { iso639_3: 'fon', iso639_2: '', name: 'Fon' },
  { iso639_3: 'fur', iso639_2: '', name: 'Friulian' },
  { iso639_3: 'gil', iso639_2: '', name: 'Kiribati' },
  { iso639_3: 'grc', iso639_2: '', name: 'Greek, Ancient' },
  { iso639_3: 'hai', iso639_2: '', name: 'Haida' },
  { iso639_3: 'haw', iso639_2: '', name: 'Hawaiian' },
  { iso639_3: 'hil', iso639_2: '', name: 'Hiligaynon' },
  { iso639_3: 'iba', iso639_2: '', name: 'Iban' },
  { iso639_3: 'ilo', iso639_2: '', name: 'Ilocano' },
  { iso639_3: 'inh', iso639_2: '', name: 'Ingush' },
  { iso639_3: 'jbo', iso639_2: '', name: 'Lojban' },
  { iso639_3: 'kab', iso639_2: '', name: 'Kabyle' },
  { iso639_3: 'krc', iso639_2: '', name: 'Balkar' },
  { iso639_3: 'kum', iso639_2: '', name: 'Kumyk' },
  { iso639_3: 'lad', iso639_2: '', name: 'Ladino' },
  { iso639_3: 'lah', iso639_2: '', name: 'Lahnda' },
  { iso639_3: 'lez', iso639_2: '', name: 'Lezgi' },
  { iso639_3: 'lus', iso639_2: '', name: 'Mizo' },
  { iso639_3: 'mad', iso639_2: '', name: 'Madura' },
  { iso639_3: 'mag', iso639_2: '', name: 'Magahi' },
  { iso639_3: 'mai', iso639_2: '', name: 'Maithili' },
  { iso639_3: 'mak', iso639_2: '', name: 'Makasar' },
  { iso639_3: 'men', iso639_2: '', name: 'Mende' },
  { iso639_3: 'mic', iso639_2: '', name: 'Micmac' },
  { iso639_3: 'min', iso639_2: '', name: 'Minangkabau' },
  { iso639_3: 'moh', iso639_2: '', name: 'Mohawk' },
  { iso639_3: 'mos', iso639_2: '', name: 'Mòoré' },
  { iso639_3: 'mwl', iso639_2: '', name: 'Mirandese' },
  { iso639_3: 'myv', iso639_2: '', name: 'Erzya' },
  { iso639_3: 'nap', iso639_2: '', name: 'Napoletano-Calabrese' },
  { iso639_3: 'new', iso639_2: '', name: 'Newar' },
  { iso639_3: 'nog', iso639_2: '', name: 'Nogai' },
  { iso639_3: 'nso', iso639_2: '', name: 'Sotho, Northern' },
  { iso639_3: 'pag', iso639_2: '', name: 'Pangasinan' },
  { iso639_3: 'pam', iso639_2: '', name: 'Pampangan' },
  { iso639_3: 'pap', iso639_2: '', name: 'Papiamentu' },
  { iso639_3: 'raj', iso639_2: '', name: 'Rajasthani' },
  { iso639_3: 'rap', iso639_2: '', name: 'Rapa Nui' },
  { iso639_3: 'rom', iso639_2: '', name: 'Romani' },
  { iso639_3: 'sah', iso639_2: '', name: 'Yakut' },
  { iso639_3: 'sat', iso639_2: '', name: 'Santali' },
  { iso639_3: 'scn', iso639_2: '', name: 'Sicilian' },
  { iso639_3: 'sco', iso639_2: '', name: 'Scots' },
  { iso639_3: 'shn', iso639_2: '', name: 'Shan' },
  { iso639_3: 'srn', iso639_2: '', name: 'Sranan' },
  { iso639_3: 'syr', iso639_2: '', name: 'Syriac' },
  { iso639_3: 'tem', iso639_2: '', name: 'Themne' },
  { iso639_3: 'tet', iso639_2: '', name: 'Tetun' },
  { iso639_3: 'tiv', iso639_2: '', name: 'Tiv' },
  { iso639_3: 'tpi', iso639_2: '', name: 'Tok Pisin' },
  { iso639_3: 'tyv', iso639_2: '', name: 'Tuva' },
  { iso639_3: 'udm', iso639_2: '', name: 'Udmurt' },
  { iso639_3: 'umb', iso639_2: '', name: 'Umbundu' },
  { iso639_3: 'vec', iso639_2: '', name: 'Venetian' },
  { iso639_3: 'wal', iso639_2: '', name: 'Wolaytta' },
  { iso639_3: 'war', iso639_2: '', name: 'Waray-Waray' },
  { iso639_3: 'yao', iso639_2: '', name: 'Yao' },
  { iso639_3: 'yap', iso639_2: '', name: 'Yapese' },
  { iso639_3: 'zap', iso639_2: '', name: 'Zapotec' },
  { iso639_3: 'zza', iso639_2: '', name: 'Zaza' }
];

/**
 * Look up a LanguageEntry by either its 2-letter or 3-letter code
 * (in that order of preference). Returns undefined when nothing
 * matches. Case-insensitive on input; the codes in LANGUAGES are
 * always lowercase.
 */
export function findLanguage(code: string): LanguageEntry | undefined {
  if (!code) { return undefined; }
  const k = String(code).trim().toLowerCase();
  if (!k) { return undefined; }
  if (k.length === 2) {
    return LANGUAGES.find(l => l.iso639_2 === k);
  }
  return LANGUAGES.find(l => l.iso639_3 === k) || LANGUAGES.find(l => l.iso639_2 === k);
}

